// Configuration service for environment-specific settings
import { createLogger } from '@/utils/logger';

const logger = createLogger('Config');

class ConfigService {
  readonly environment = import.meta.env.MODE;
  readonly viteEnvironment = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE;
  readonly isLocal = this.viteEnvironment === 'local';
  readonly isDevelopment = this.viteEnvironment === 'development';
  readonly isStaging = this.viteEnvironment === 'staging';
  readonly isProduction = this.viteEnvironment === 'production';
  
  // API Configuration - environment-specific defaults
  readonly apiBaseUrl = import.meta.env.VITE_API_BASE_URL || this.getDefaultApiUrl();
  readonly wsBaseUrl = import.meta.env.VITE_WS_BASE_URL || this.apiBaseUrl.replace('http', 'ws');

  // Public site (customer-facing) base URL used for deep links from Ops (e.g. open forum post).
  readonly publicSiteBaseUrl =
    import.meta.env.VITE_PUBLIC_SITE_BASE_URL || this.getDefaultPublicSiteBaseUrl();
  
  // Authentication Configuration
  readonly googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
  readonly adminAuthDomain = import.meta.env.VITE_ADMIN_AUTH_DOMAIN || 'nurie.ai';
  readonly defaultAdminEmail = import.meta.env.VITE_DEFAULT_ADMIN || '';
  readonly sessionTimeoutHours = parseInt(import.meta.env.VITE_SESSION_TIMEOUT_HOURS || '72');
  readonly sessionTimeoutMs = this.sessionTimeoutHours * 60 * 60 * 1000;
  
  // Application Configuration
  readonly appName = import.meta.env.VITE_APP_NAME || 'MediaSage Ops Dashboard';
  readonly appVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';
  readonly logLevel = import.meta.env.VITE_LOG_LEVEL || this.getDefaultLogLevel();
  
  // Feature Flags
  readonly enableAnalytics = import.meta.env.VITE_ENABLE_ANALYTICS !== 'false';
  readonly enableExport = import.meta.env.VITE_ENABLE_EXPORT !== 'false';
  readonly enableRealTime = import.meta.env.VITE_ENABLE_REAL_TIME !== 'false';

  /**
   * Get default log level based on environment
   */
  private getDefaultLogLevel(): string {
    if (this.isDevelopment) {
      return 'info';  // Changed from debug to info to reduce chattiness
    } else if (this.isStaging) {
      return 'info';
    } else {
      return 'error';
    }
  }

  /**
   * Get default API URL based on environment
   */
  private getDefaultApiUrl(): string {
    if (this.isProduction) {
      return 'https://api.media.nurie.ai';
    } else if (this.isStaging) {
      return 'https://staging.api.media.nurie.ai';
    } else if (this.viteEnvironment === 'local') {
      return 'http://localhost:8000';
    } else {
      return 'https://dev.api.media.nurie.ai';
    }
  }

  /**
   * Get default public site URL based on environment
   */
  private getDefaultPublicSiteBaseUrl(): string {
    if (this.isProduction) {
      return 'https://media.nurie.ai';
    } else if (this.isStaging) {
      return 'https://staging.media.nurie.ai';
    } else if (this.viteEnvironment === 'local') {
      // Matches mediahub-web local dev default
      return 'http://localhost:5173';
    } else {
      return 'https://dev.media.nurie.ai';
    }
  }

  /**
   * Get environment display name
   */
  getEnvironmentDisplayName(): string {
    switch (this.viteEnvironment) {
      case 'local':
        return 'Local';
      case 'development':
        return 'Development';
      case 'staging':
        return 'Staging';
      case 'production':
        return 'Production';
      default:
        return 'Unknown';
    }
  }

  /**
   * Check if current environment is non-production
   */
  isNonProduction(): boolean {
    return this.isLocal || this.isDevelopment || this.isStaging;
  }

  /**
   * Validate required configuration values
   */
  validateConfig(): { valid: boolean; errors: string[]; warnings: string[] } {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    // Validate required configuration
    if (!this.apiBaseUrl) {
      errors.push('VITE_API_BASE_URL is not configured');
    }

    if (!this.publicSiteBaseUrl) {
      warnings.push('VITE_PUBLIC_SITE_BASE_URL is not configured; using environment default');
    }
    
    // Environment-specific validation
    if (this.isProduction) {
      if (!this.googleClientId) {
        errors.push('VITE_GOOGLE_CLIENT_ID is required for production');
      }
      if (!this.defaultAdminEmail) {
        warnings.push('VITE_DEFAULT_ADMIN is not set for production');
      }
    } else if (this.isStaging) {
      if (!this.googleClientId) {
        warnings.push('VITE_GOOGLE_CLIENT_ID is not set for staging');
      }
    }
    
    // Validate environment value
    const validEnvironments = ['local', 'development', 'staging', 'production'];
    if (!validEnvironments.includes(this.viteEnvironment)) {
      errors.push(`Invalid VITE_ENVIRONMENT: ${this.viteEnvironment}. Must be one of: ${validEnvironments.join(', ')}`);
    }
    
    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Get all configuration values (for debugging)
   */
  getAllConfig() {
    return {
      environment: this.environment,
      viteEnvironment: this.viteEnvironment,
      environmentName: this.getEnvironmentDisplayName(),
      isLocal: this.isLocal,
      isDevelopment: this.isDevelopment,
      isStaging: this.isStaging,
      isProduction: this.isProduction,
      isNonProduction: this.isNonProduction(),
      apiBaseUrl: this.apiBaseUrl,
      wsBaseUrl: this.wsBaseUrl,
      publicSiteBaseUrl: this.publicSiteBaseUrl,
      appName: this.appName,
      appVersion: this.appVersion,
      logLevel: this.logLevel,
      features: {
        analytics: this.enableAnalytics,
        export: this.enableExport,
        realTime: this.enableRealTime,
      },
    };
  }

  /**
   * Log configuration status on initialization
   */
  logConfigStatus(): void {
    const validation = this.validateConfig();
    
    // Only log detailed config in development
    if (this.isDevelopment) {
      logger.info(`${this.getEnvironmentDisplayName()} environment configuration:`, this.getAllConfig());
    } else if (!this.isProduction) {
      logger.info(`${this.getEnvironmentDisplayName()} environment initialized`);
    }
    
    if (validation.warnings.length > 0) {
      logger.warn('Configuration warnings:', validation.warnings);
    }
    
    if (!validation.valid) {
      logger.error('Configuration errors:', validation.errors);
    } else if (this.isDevelopment) {
      logger.info(`${this.getEnvironmentDisplayName()} configuration validated successfully`);
    }
  }
}

export const configService = new ConfigService();

// Initialize configuration logging
configService.logConfigStatus();

export default configService;
